import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Boxes, Check, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Pick FG batches for one sales-invoice line. FIFO is pre-filled; the operator can move qty between batches.
export const fifoPlan = (batches, qty) => {
  let left = parseFloat(qty) || 0;
  const plan = [];
  for (const b of batches || []) {
    if (left <= 0) break;
    const avail = parseFloat(b.quantity) || 0;
    if (avail <= 0) continue;
    const take = Math.min(avail, left);
    plan.push({ batch_id: b.batch_id, quantity: +take.toFixed(3), manual: true });
    left -= take;
  }
  return plan;
};

export const BatchPicker = ({ item, product, onChange, testId, onSynced }) => {
  const [open, setOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const batchQty = (b) => parseFloat(b.quantity ?? b.output_kg ?? b.qty) || 0;      // legacy rows wrote output_kg
  const batches = (product?.batch_ids || []).filter(b => batchQty(b) > 0).map(b => ({ ...b, quantity: batchQty(b) }));
  const stock = parseFloat(product?.current_stock) || 0;
  const batchSum = batches.reduce((s, b) => s + b.quantity, 0);
  const outOfSync = product && Math.abs(stock - batchSum) > 0.005;
  const qty = parseFloat(item.quantity) || 0;
  const picks = (item.batches_dispatched || []).filter(p => p.manual);
  const plan = picks.length ? picks : fifoPlan(batches, qty);
  const picked = plan.reduce((s, p) => s + (parseFloat(p.quantity) || 0), 0);
  const short = Math.max(0, +(qty - picked).toFixed(3));

  const setQty = (batchId, v) => {
    const next = plan.filter(p => p.batch_id !== batchId);
    const q = parseFloat(v) || 0;
    if (q > 0) next.push({ batch_id: batchId, quantity: q, manual: true });
    onChange(next.sort((a, b) => batches.findIndex(x => x.batch_id === a.batch_id) - batches.findIndex(x => x.batch_id === b.batch_id)));
  };
  const sync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/fg-batches/sync`, { fg_id: product.id, reason: 'sales voucher' }, { withCredentials: true });
      toast.success(`${product.name}: batches now match Inventory (${stock} ${product.unit || 'kg'})`);
      onSynced?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  if (!product) return null;
  return (
    <div className="mt-0.5 text-[10px]" data-testid={testId}>
      <button type="button" onClick={() => setOpen(o => !o)} data-testid={`${testId}-toggle`}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${short > 0 ? 'border-red-300 text-red-700 bg-red-50' : 'border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100'}`}>
        <Boxes size={10} />
        {batches.length === 0 ? `No batches in stock (Inventory shows ${stock} ${product.unit || 'kg'})` : plan.length ? plan.map(p => `${p.batch_id} ×${p.quantity}`).join(' · ') : 'Pick batches'}
        {short > 0 && batches.length > 0 && ` · short ${short}`}
        {picks.length > 0 && <Check size={10} className="text-emerald-600" />}
      </button>
      {outOfSync && (
        <button type="button" onClick={sync} disabled={syncing} data-testid={`${testId}-sync`}
          className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-50">
          <RefreshCw size={10} /> {syncing ? 'Syncing…' : `Batches ${batchSum.toFixed(2)} ≠ stock ${stock} — sync`}
        </button>
      )}
      {open && batches.length > 0 && (
        <div className="mt-1 border border-gray-300 rounded bg-white p-2 w-72 shadow-lg" data-testid={`${testId}-panel`}>
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-gray-700">Batches for {product.name}</span>
            <button type="button" className="text-blue-700 hover:underline" onClick={() => onChange(fifoPlan(batches, qty))} data-testid={`${testId}-fifo`}>Reset FIFO</button>
          </div>
          <table className="w-full">
            <tbody>
              {batches.map(b => {
                const cur = plan.find(p => p.batch_id === b.batch_id);
                return (
                  <tr key={b.batch_id} className="border-t border-gray-100">
                    <td className="py-1 font-mono text-gray-800">{b.batch_id}</td>
                    <td className="py-1 text-right text-gray-500">avail {b.quantity}</td>
                    <td className="py-1 text-right">
                      <input type="number" min="0" max={b.quantity} step="0.001" value={cur ? cur.quantity : ''} placeholder="0"
                        onChange={e => setQty(b.batch_id, Math.min(parseFloat(e.target.value) || 0, parseFloat(b.quantity) || 0))}
                        className="w-16 border border-gray-300 rounded px-1 py-0.5 text-right font-mono text-gray-900" data-testid={`${testId}-qty-${b.batch_id}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className={`mt-1 text-right ${short > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
            picked {picked.toFixed(3)} / {qty} {item.unit || 'kg'}{short > 0 ? ` — ${short} will be taken FIFO` : ''}
          </div>
        </div>
      )}
    </div>
  );
};

export const BatchChips = ({ batches, light = false }) => {
  if (!batches?.length) return null;
  const cls = light ? 'bg-amber-100 text-amber-900 border-amber-300' : 'bg-amber-500/10 text-amber-300 border-amber-500/30';
  return (
    <div className="flex flex-wrap gap-1 mt-0.5">
      {batches.map((b, i) => (
        <span key={i} className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${cls}`} data-testid={`batch-chip-${b.batch_id}`}>
          {b.batch_id} ×{b.quantity}{b.manual ? '' : ' (FIFO)'}
        </span>
      ))}
    </div>
  );
};
